/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { AxiosResponse } from 'axios';
import _ from 'lodash';

import { api } from '@/api';
import { ValuesMap } from '@/common/types';
import { ReportSummaryContentItem, ReportSummaryResponse } from '@/modules/Reports/report.types';

import { useReportSummaryBase } from '../hooks';

const PER_PAGE = 45;

interface SummaryParams {
  disabled?: boolean;
  isMultipleEmployers?: boolean;
  isWithTotal?: boolean;
  employerId?: string;
  employerIds?: string[];
}

const useBaseReportSummaryQuery = <T extends ReportSummaryResponse = ReportSummaryResponse>(
  path: string,
  queryArray: any[],
  params: ValuesMap<any>,
  summaryParams?: SummaryParams
) => {
  const [page, setPage] = useState(0);
  const [accumulatedContent, setAccumulatedContent] = useState<ReportSummaryContentItem[]>([]);
  const [savedHeaders, setSavedHeaders] = useState<any[]>([]);
  const [savedPlans, setSavedPlans] = useState<any[]>([]);
  const [savedLastUpdatedAt, setSavedLastUpdatedAt] = useState<string | undefined>();

  const { data, isLoading, isSuccess, isFetching } = useQuery<
    AxiosResponse<T>,
    unknown,
    AxiosResponse<T>,
    any[]
  >(
    [...queryArray, page],
    ({ signal }) =>
      api.get<T>(path, {
        signal,
        params: _.pickBy({
          size: PER_PAGE,
          page,
          ...params,
        }),
      }),
    {
      onSuccess: (data) => {
        const content = data?.data?.content;
        const pageNumber = data?.data?.page_number || 0;

        if (pageNumber === 0 || !savedHeaders.length) {
          setSavedHeaders(data?.data?.headers || []);
          setSavedPlans(data?.data?.plans || []);
          setSavedLastUpdatedAt(data?.data?.last_updated_at);
        }

        const contentList: ReportSummaryContentItem[] = content
          ? Array.isArray(content)
            ? content
            : [content]
          : [];

        if (pageNumber === 0) {
          setAccumulatedContent(contentList);
        } else {
          setAccumulatedContent((prev) => [...prev, ...contentList]);
        }
      },
      cacheTime: 0,
      enabled: summaryParams?.disabled
        ? false
        : !!params.employer_id || !!params.employer_ids?.length || !!summaryParams?.isMultipleEmployers,
    }
  );

  const isHasNextPage = useMemo(() => data?.data?.has_next || false, [data?.data?.has_next]);
  const total = useMemo(() => data?.data?.total_records || 0, [data?.data?.total_records]);

  const stableContent = useMemo(() => {
    if (!accumulatedContent.length) return undefined;
    return accumulatedContent.length > 1 ? accumulatedContent : accumulatedContent[0];
  }, [accumulatedContent]);

  const { planOptions, formattedData, lastTimeUpdateDate, isEmptyData, isEmptyPlanOptions } =
    useReportSummaryBase({
      headers: savedHeaders.length ? savedHeaders : data?.data?.headers,
      plans: savedPlans.length ? savedPlans : data?.data?.plans,
      last_updated_at: savedLastUpdatedAt || data?.data?.last_updated_at,
      content: stableContent,
      isWithTotal: summaryParams?.isWithTotal,
      isMultipleEmployer: summaryParams?.isMultipleEmployers,
      employerId: summaryParams?.employerId,
      employerIds: summaryParams?.employerIds,
      isLoading: isLoading && page === 0,
    });

  const handleLoadMore = useCallback(() => {
    if (isHasNextPage && !isLoading && !isFetching) {
      setPage((prev) => prev + 1);
    }
  }, [isHasNextPage, isLoading, isFetching]);

  const resetPagination = useCallback(() => {
    setPage(0);
    setAccumulatedContent([]);
    setSavedHeaders([]);
    setSavedPlans([]);
    setSavedLastUpdatedAt(undefined);
  }, []);

  return {
    data: formattedData,
    rawData: data,
    isLoading: (isFetching || isLoading) && page === 0,
    isLoadingMore: isFetching && page > 0,
    isSuccess,
    isFetching,
    planOptions,
    isEmptyPlanOptions,
    lastTimeUpdateDate,
    isEmptyData,
    isHasNextPage,
    total,
    handleLoadMore,
    resetPagination,
    setPage,
    setAccumulatedContent,
  };
};

export default useBaseReportSummaryQuery;
